import { offlineFetch, OfflineLayerHttpError } from '@/lib/offline-api-layer';
import { useAuthStore } from '@/stores/auth-store';

const API_BASE = '/api/v1';

/**
 * API error with optional error code for i18n.
 * Frontend can use: t(`error.${apiError.code}`) for localized messages.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string | undefined,
    public readonly statusCode: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract error.code from a NestJS error response body.
 *
 * H1 CR patch (STORY-1306): NestJS controllers emit `{ error: { code, message } }`
 * (nested) via `throw new Conflict/BadRequestException({ error: { code, ... } })`.
 * Legacy controllers may emit `{ code, message }` (flat) directly. Read both
 * shapes so error UX works across both conventions.
 */
export function extractErrorBody(body: unknown): {
  code: string | undefined;
  message: string | undefined;
} {
  if (!body || typeof body !== 'object') {
    return { code: undefined, message: undefined };
  }
  const flat = body as { code?: string; message?: string; error?: unknown };
  const nested =
    flat.error && typeof flat.error === 'object'
      ? (flat.error as { code?: string; message?: string })
      : undefined;
  return {
    code: nested?.code ?? flat.code,
    message: nested?.message ?? flat.message,
  };
}

interface RequestOptions extends RequestInit {
  auth?: boolean;
  tenantId?: string;
  _isRetry?: boolean; // Internal flag for token refresh retry
}

class ApiClient {
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  private getHeaders(options: RequestOptions = {}): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options.auth !== false) {
      const { accessToken, user } = useAuthStore.getState();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      // Add tenant ID from explicit option or user context
      const tenantId = options.tenantId ?? (user as { tenantId?: string } | null)?.tenantId;
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }

    return headers;
  }

  /**
   * Attempt to refresh the access token using the refresh token (httpOnly cookie).
   * M-005 SECURITY FIX: The refresh token is in an httpOnly cookie — the browser
   * sends it automatically with `credentials: 'include'`. No JS access needed.
   *
   * Returns true if refresh succeeded, false otherwise.
   */
  private async refreshToken(): Promise<boolean> {
    // Prevent multiple concurrent refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefreshToken();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(): Promise<boolean> {
    const { setAccessToken, logout } = useAuthStore.getState();

    try {
      // M-005: No body needed — refresh token comes from httpOnly cookie.
      // credentials: 'include' ensures the cookie is sent cross-origin.
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        // Refresh token invalid/expired - logout user
        logout();
        window.location.href = '/login';
        return false;
      }

      const data = await response.json();
      // Backend wraps response in { data: { accessToken } }
      // (refreshToken is rotated in the httpOnly cookie, not in the body)
      setAccessToken(data.data.accessToken);
      return true;
    } catch {
      logout();
      window.location.href = '/login';
      return false;
    }
  }

  /**
   * Handle 401 Unauthorized response
   * Attempts token refresh, or logs out if refresh fails
   *
   * @param overrideBody - If provided, use this as the request body instead of options.body
   *                       (needed for FormData uploads where body is not in RequestOptions)
   * @param overrideHeaders - If provided, use this headers builder instead of getHeaders()
   *                          (needed for FormData uploads that must NOT set Content-Type)
   */
  private async handle401(
    endpoint: string,
    options: RequestOptions,
    overrideBody?: globalThis.BodyInit,
    overrideHeaders?: () => Record<string, string>
  ): Promise<Response | null> {
    // Don't retry if this was already a retry or if auth is disabled
    if (options._isRetry || options.auth === false) {
      return null;
    }

    // Try to refresh the token
    const refreshed = await this.refreshToken();

    if (refreshed) {
      // Retry the original request with new token
      const headers = overrideHeaders
        ? overrideHeaders()
        : this.getHeaders({ ...options, _isRetry: true });
      const retryOptions: RequestInit = {
        ...options,
        headers,
        body: overrideBody ?? options.body,
      };
      return fetch(`${API_BASE}${endpoint}`, retryOptions);
    }

    return null;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    const method = options.method?.toUpperCase() ?? 'GET';

    // GET requests use offline-aware fetch (IndexedDB fallback)
    if (method === 'GET') {
      try {
        return await offlineFetch<T>(url, {
          ...options,
          headers: this.getHeaders(options),
        });
      } catch (error) {
        // Fix #3: Handle 401 from offlineFetch — trigger token refresh
        if (
          error instanceof OfflineLayerHttpError &&
          error.status === 401 &&
          options.auth !== false &&
          !options._isRetry
        ) {
          const retryResponse = await this.handle401(endpoint, options);
          if (retryResponse) {
            if (!retryResponse.ok) throw new Error(`HTTP ${retryResponse.status}`);
            return retryResponse.json() as Promise<T>;
          }
          throw new Error('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
        }
        throw error;
      }
    }

    // Mutations go through normal fetch (SW BackgroundSync handles offline)
    let response = await fetch(url, {
      ...options,
      headers: this.getHeaders(options),
    });

    // Handle 401 - attempt token refresh
    if (response.status === 401 && options.auth !== false && !options._isRetry) {
      const retryResponse = await this.handle401(endpoint, options);
      if (retryResponse) {
        response = retryResponse;
      } else {
        // Refresh failed - user will be logged out and redirected
        throw new Error('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: 'Request failed' }));
      const { code, message } = extractErrorBody(body);
      throw new ApiError(message || `HTTP ${response.status}`, code, response.status);
    }

    return response.json();
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload file via FormData (multipart/form-data).
   * Does NOT set Content-Type — browser sets it with boundary automatically.
   * Handles 401 with token refresh (same pattern as request()).
   */
  async upload<T>(endpoint: string, formData: FormData, options?: RequestOptions): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    const buildHeaders = (): Record<string, string> => {
      const headers: Record<string, string> = {};
      if (options?.auth !== false) {
        const { accessToken, user } = useAuthStore.getState();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
        const tenantId = options?.tenantId ?? (user as { tenantId?: string } | null)?.tenantId;
        if (tenantId) {
          headers['X-Tenant-ID'] = tenantId;
        }
      }
      return headers;
    };

    let response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: formData,
    });

    // Handle 401 — attempt token refresh, preserving FormData body and omitting Content-Type
    // (M-001/M-008 fix: pass FormData body and upload-specific headers to handle401)
    if (response.status === 401 && options?.auth !== false) {
      const retryResponse = await this.handle401(
        endpoint,
        { ...options, method: 'POST' },
        formData,
        buildHeaders
      );
      if (retryResponse) {
        response = retryResponse;
      } else {
        throw new Error('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: 'Upload failed' }));
      const { code, message } = extractErrorBody(body);
      throw new ApiError(message || `HTTP ${response.status}`, code, response.status);
    }

    return response.json();
  }

  /**
   * Download a binary/blob response (e.g. PDF).
   * Uses proper auth headers and handles 401 token refresh.
   * (M-002/M-003 fix: centralized blob download with auth)
   */
  async downloadBlob(endpoint: string, options?: RequestOptions): Promise<Blob> {
    const { blob } = await this.downloadBlobWithHeaders(endpoint, options);
    return blob;
  }

  /**
   * Download a binary payload along with its response headers. Used by the
   * Story 94-9b DocumentsPanel so the client can read `X-Content-SHA256` and
   * verify the hash before handing the blob to the user.
   */
  async downloadBlobWithHeaders(
    endpoint: string,
    options?: RequestOptions
  ): Promise<{ blob: Blob; headers: Headers }> {
    const url = `${API_BASE}${endpoint}`;
    const headers: Record<string, string> = {};

    if (options?.auth !== false) {
      const { accessToken, user } = useAuthStore.getState();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      const tenantId = options?.tenantId ?? (user as { tenantId?: string } | null)?.tenantId;
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }
    }

    let response = await fetch(url, { ...options, headers });

    // Handle 401 — attempt token refresh
    if (response.status === 401 && options?.auth !== false && !options?._isRetry) {
      const retryResponse = await this.handle401(endpoint, { ...options });
      if (retryResponse) {
        response = retryResponse;
      } else {
        throw new Error('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
      }
    }

    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}`, undefined, response.status);
    }

    return { blob: await response.blob(), headers: response.headers };
  }
}

export const api = new ApiClient();
